import { useContext } from 'react';
import { useToggle } from 'usehooks-ts';
import { LinkProps, NavLink } from 'react-router-dom';
import { SvgIcon } from '@progress/kendo-react-common';
import { circleIcon } from '@progress/kendo-svg-icons';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import { LayoutContext } from '../contexts/layout-provider';
import { NotificationIndicator } from './notification-indicator';

interface MenuItemProps extends LinkProps {
  icon: string;
  label: string;
  end?: boolean;
  expanded?: boolean;
  notificationCount?: number;
  hasNotification?: boolean;
  enableOpenNewWindow?: boolean;
  iconSet?: string | 'outlined' | 'light';
}

const MenuItem = ({
  to,
  end,
  icon,
  label,
  children,
  expanded = false,
  iconSet = 'outlined',
  notificationCount = 0,
  hasNotification = false,
  enableOpenNewWindow = false,
  ...props
}: MenuItemProps) => {
  const layout = useContext(LayoutContext);
  const [isExpanded, setIsExpanded] = useToggle(expanded);
  const chevronIcon = isExpanded ? 'expand_more' : 'chevron_right';
  const chevronClass = isExpanded
    ? 'aside-menu__sub--expanded'
    : 'aside-menu__sub--collapsed';

  const Item = (
    <div className={'d-flex justify-content-between'}>
      <NavLink
        to={to}
        onClick={() => layout.setActiveMenu(label)}
        end={end ?? true}
        {...(layout.navIsCollapsed &&
          enableOpenNewWindow && {
            target: '_blank'
          })}
        {...props}
        className={`aside-menu__item ${
          children ? 'aside-menu__parent' : (props.className ?? '')
        }`.trim()}>
        <span className={'item__icon'}>
          <span
            className={`material-symbols-outlined material-symbols-outlined--${iconSet}`}>
            {icon}
          </span>
          {notificationCount > 0 && layout.navIsCollapsed && (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0
              }}>
              <NotificationIndicator
                counter={notificationCount}
                bgColor={'#FF5527'}
              />
            </div>
          )}
        </span>

        <div className="d-flex align-items-center justify-content-between w-100 pr-3">
          <span className={'item__label'}>{label}</span>
          {notificationCount > 0 && !layout.navIsCollapsed && (
            <NotificationIndicator
              counter={notificationCount}
              bgColor={'#FF5527'}
            />
          )}
        </div>

        {children && (
          <span className={'item__icon'}>
            <span
              className={'material-symbols-outlined'}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsExpanded();
              }}>
              {chevronIcon}
            </span>
          </span>
        )}
      </NavLink>

      {enableOpenNewWindow && (
        <NavLink
          to={to}
          target="_blank"
          className={'text-primary d-flex align-items-center overflow-hidden'}>
          <div
            className={
              'd-flex align-items-center justify-content-center px-3 text-primary'
            }>
            <span className={'material-symbols-outlined p-0 m-0'}>
              open_in_new
            </span>
          </div>
        </NavLink>
      )}
    </div>
  );

  return (
    <>
      {layout.navIsCollapsed ? (
        <OverlayTrigger
          placement={'right'}
          overlay={
            <Popover
              className="border-1"
              style={{
                borderRadius: '0.25rem',
                marginLeft: '0.25rem'
              }}>
              <Popover.Body className="px-2 py-1">{label}</Popover.Body>
            </Popover>
          }>
          {Item}
        </OverlayTrigger>
      ) : (
        Item
      )}

      {children && (
        <div className={`aside-menu__sub ${chevronClass}`}>{children}</div>
      )}
    </>
  );
};

export default MenuItem;
